import { Cart, CartService } from '@services/cart';
import { getCartDraft } from '@utils/getCartDraft';
import Cookies from 'js-cookie';
import { useServiceRef } from 'hooks';
import { ProductService } from '@services/product';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { sendCXProvideRecommendationEvent } from './sendCXProvideRecommendationEvent';

interface AgentStudioCartLineDetail {
  productId: string;
  quantity: number;
  name?: string;
  price?: unknown;
  currency?: string;
}

export const handleCartUpdate = async (event: Event, setCart: (cart: Cart) => void): Promise<void> => {
  const { productId, quantity, name } = (event as CustomEvent<AgentStudioCartLineDetail>).detail;
  if (!productId) return;

  const cartServiceRef = useServiceRef(CartService);
  const productServiceRef = useServiceRef(ProductService);

  try {
    let cart = await cartServiceRef.current.getMyActiveCart(JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || '{}')?.key);
    const productInCart = cart?.lineItems?.find(item => item.productId === productId);
    if (quantity === 0 && !productInCart) return;

    try {
      let updatedCart: Cart | null = null;

      if (quantity === 0 && productInCart && cart) {
        updatedCart = await cartServiceRef.current.removeLineItem(
          cart.id,
          cart.version,
          productInCart.id,
        );
      } else {
        const productDetails = await productServiceRef.current.getProductData(productId);

        if (!productDetails) return;

        const variantId = productDetails?.variants?.[0]?.id;
        if (!variantId) return;

        const lineItemDraft = { productId, variantId, quantity };

        if (!cart) {
          const cartDraft = getCartDraft(
            lineItemDraft,
            productDetails?.price?.currencyCode,
          );
          updatedCart = await cartServiceRef.current.createCart(cartDraft);
          if (updatedCart) sendCXProvideRecommendationEvent(name ?? productDetails?.name);
        } else if (productInCart) {
          updatedCart = await cartServiceRef.current.changeLineItemQuantity(
            cart.id,
            cart.version,
            productInCart.id,
            quantity,
          );
        } else {
          updatedCart = await cartServiceRef.current.addLineItem(
            cart.id,
            cart.version,
            lineItemDraft,
          );
          if (updatedCart) sendCXProvideRecommendationEvent(name ?? productDetails?.name);
        }
      }

      if (!updatedCart) return;

      cart = updatedCart;
      setCart(cart);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Cart update failed',
        error instanceof Error ? error.message : error,
      );

      return;
    }
  } catch {
    // eslint-disable-next-line no-console
    console.error('Failed to update cart');

    return;
  }
};
